import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  CircularProgress
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { makeStyles } from '@material-ui/core/styles';
import {
  DeleteForever as DeleteIcon,
  CheckCircle as CheckIcon
} from '@material-ui/icons';

const useStyles = makeStyles((theme) => ({
  container: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100vh',
    backgroundColor: theme.palette.background.default,
    padding: theme.spacing(2)
  },
  paper: {
    padding: theme.spacing(4),
    maxWidth: 600,
    width: '100%'
  },
  header: {
    textAlign: 'center',
    marginBottom: theme.spacing(3)
  },
  iconContainer: {
    textAlign: 'center',
    marginBottom: theme.spacing(2)
  },
  deleteIcon: {
    fontSize: 64,
    color: theme.palette.error.main
  },
  successIcon: {
    fontSize: 64,
    color: theme.palette.success.main
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(3)
  },
  section: {
    marginBottom: theme.spacing(3)
  },
  buttonContainer: {
    display: 'flex',
    gap: theme.spacing(2),
    justifyContent: 'center'
  }
}));

const FacebookDataDeletion = () => {
  const classes = useStyles();
  const [step, setStep] = useState('form'); // 'form' | 'processing' | 'success'
  const [formData, setFormData] = useState({
    userId: '',
    email: '',
    reason: ''
  });

  const handleInputChange = (field) => (event) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const handleSubmitDeletionRequest = async () => {
    setStep('processing');
    
    try {
      // Aquí puedes implementar la lógica para procesar la solicitud
      // Por ejemplo, enviar a tu backend o a una API
      
      // Simular procesamiento
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Para propósitos de demostración, guardamos en localStorage
      const deletionRequest = {
        ...formData,
        timestamp: new Date().toISOString(),
        status: 'pending',
        confirmationCode: 'DEL-' + Math.random().toString(36).substr(2, 9).toUpperCase()
      };
      
      localStorage.setItem('facebook_deletion_request', JSON.stringify(deletionRequest));
      
      setStep('success');
    } catch (error) {
      console.error('Error processing deletion request:', error);
      alert('Error procesando la solicitud. Por favor intenta nuevamente.');
      setStep('form');
    }
  };

  const renderForm = () => (
    <>
      <Box className={classes.iconContainer}>
        <DeleteIcon className={classes.deleteIcon} />
      </Box>
      
      <Typography variant="h4" className={classes.header}>
        Solicitud de Eliminación de Datos
      </Typography>

      <Alert severity="info" className={classes.section}>
        <Typography variant="body2">
          Esta página permite solicitar la eliminación de tus datos asociados con nuestra aplicación de Facebook.
          Una vez procesada, eliminaremos toda la información personal que tengamos almacenada.
        </Typography>
      </Alert>

      <Box className={classes.section}>
        <Typography variant="h6" gutterBottom>
          Información requerida:
        </Typography>
        
        <form className={classes.form}>
          <TextField
            label="ID de Usuario de Facebook"
            variant="outlined"
            value={formData.userId}
            onChange={handleInputChange('userId')}
            placeholder="Tu Facebook User ID"
            helperText="Puedes encontrar tu ID en la URL cuando Facebook redirija aquí"
            required
          />
          
          <TextField
            label="Email de contacto"
            variant="outlined"
            type="email"
            value={formData.email}
            onChange={handleInputChange('email')}
            placeholder="tu-email@ejemplo.com"
            helperText="Para confirmación de la eliminación"
            required
          />
          
          <TextField
            label="Razón para la eliminación (opcional)"
            variant="outlined"
            multiline
            rows={3}
            value={formData.reason}
            onChange={handleInputChange('reason')}
            placeholder="Cuéntanos por qué quieres eliminar tus datos"
          />
        </form>
      </Box>

      <Box className={classes.section}>
        <Typography variant="body2" color="textSecondary">
          <strong>Qué datos eliminamos:</strong>
          <br />
          • Información de perfil de Facebook
          • Tokens de acceso y autorización
          • Registros de mensajes de WhatsApp Business
          • Configuraciones de la aplicación
          • Cualquier otro dato personal asociado con tu cuenta
        </Typography>
      </Box>

      <Box className={classes.buttonContainer}>
        <Button
          variant="contained"
          color="secondary"
          onClick={handleSubmitDeletionRequest}
          disabled={!formData.userId || !formData.email}
          startIcon={<DeleteIcon />}
        >
          Solicitar Eliminación
        </Button>
      </Box>
    </>
  );

  const renderProcessing = () => (
    <>
      <Box className={classes.iconContainer}>
        <CircularProgress size={64} />
      </Box>
      
      <Typography variant="h5" className={classes.header}>
        Procesando solicitud...
      </Typography>
      
      <Typography variant="body1" align="center">
        Por favor espera mientras procesamos tu solicitud de eliminación de datos.
      </Typography>
    </>
  );

  const renderSuccess = () => (
    <>
      <Box className={classes.iconContainer}>
        <CheckIcon className={classes.successIcon} />
      </Box>
      
      <Typography variant="h4" className={classes.header}>
        Solicitud Enviada
      </Typography>

      <Alert severity="success" className={classes.section}>
        <Typography variant="body2">
          Tu solicitud de eliminación de datos ha sido recibida y procesada exitosamente.
          Recibirás una confirmación por email cuando la eliminación esté completa.
        </Typography>
      </Alert>

      <Typography variant="body2" align="center" color="textSecondary">
        Tiempo estimado de procesamiento: 30 días
      </Typography>
    </>
  );

  const renderContent = () => {
    switch (step) {
      case 'form':
        return renderForm();
      case 'processing':
        return renderProcessing();
      case 'success':
        return renderSuccess();
      default:
        return renderForm();
    }
  };

  return (
    <Box className={classes.container}>
      <Paper className={classes.paper}>
        {renderContent()}
      </Paper>
    </Box>
  );
};

export default FacebookDataDeletion;
